import React, { useCallback, useEffect, useMemo, useState } from "react";
import logo from "../../../assets/Logo/FBX_2024_alone.svg";
import { useNavBar } from "../../../Contexts/navbar.items.context.jsx";
import { getUserFromToken } from "../../../utils/functions.ts";
import "./supportAnnouncement.style.scss";

const TASKS_ENDPOINT = "https://www.fbxchange.com/ReactPHP/tasks.php";
const TASK_NOTICE_PREVIEW_EVENT = "FBX_PREVIEW_TASK_SIGNIN_NOTICE";

const getScoutName = (user) => (
    user?.displayName ||
    user?.scoutName ||
    user?.name ||
    `${user?.firstName || ""} ${user?.lastName || ""}`.trim() ||
    String(user?.scout || "")
);

const formatDate = (value) => {
    if (!value || value === "0000-00-00" || value === "0000-00-00 00:00:00") return "-";
    const normalized = String(value).replace(" ", "T");
    const date = new Date(normalized);
    if (Number.isNaN(date.getTime())) return String(value).split(" ")[0];
    return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

const isNewTask = (task) => String(task?.status || "").trim().toLowerCase() === "not started";

const TaskAnnouncement = ({ show = false }) => {
    const { updateActiveSideButton } = useNavBar();
    const [visible, setVisible] = useState(false);
    const [tasks, setTasks] = useState([]);

    const loadOpenTasks = useCallback(async ({ forcePreview = false } = {}) => {
            const activeUser = await getUserFromToken();
            if (!activeUser?.team || !activeUser?.scout) return;

            const canAssignTasks = String(activeUser?.assignTask || "").trim().toLowerCase() === "yes";
            if (forcePreview && !canAssignTasks) return;

            const loginMarker = activeUser.iat || "current";
            const storageKey = `FBX_TASKS_SIGNIN_NOTICE_${activeUser.scout}_${loginMarker}`;
            try {
                if (!forcePreview && sessionStorage.getItem(storageKey) === "Shown") return;
            } catch {}

            try {
                const response = await fetch(TASKS_ENDPOINT, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        scriptName: "getTasks",
                        team: activeUser.team,
                        scoutId: activeUser.scout,
                        scoutName: getScoutName(activeUser),
                        userContactLevel: activeUser.userContactLevel ?? activeUser.security ?? "",
                    }),
                });
                const data = await response.json();
                if (!data?.success) return;

                const scoutId = String(activeUser.scout || "");
                const newTasks = (Array.isArray(data.tasks) ? data.tasks : []).filter(isNewTask);
                const assignedNewTasks = newTasks.filter((task) => (
                    String(task.assignedToScoutId || "") === scoutId
                ));
                const visibleTasks = forcePreview && canAssignTasks ? newTasks : assignedNewTasks;

                const noticeSignature = visibleTasks
                    .map((task) => String(task.taskId || ""))
                    .filter(Boolean)
                    .sort()
                    .join(",");

                try {
                    if (!forcePreview && sessionStorage.getItem(storageKey) === noticeSignature) return;
                } catch {}

                if (visibleTasks.length > 0) {
                    setTasks(visibleTasks);
                    setVisible(true);
                    try {
                        if (!forcePreview) sessionStorage.setItem(storageKey, noticeSignature);
                    } catch {}
                } else if (forcePreview) {
                    window.alert("No not-started tasks are available to preview.");
                }
            } catch {}
    }, []);

    useEffect(() => {
        if (!show) {
            setVisible(false);
            setTasks([]);
            return undefined;
        }

        let isMounted = true;

        const loadInitialTasks = async () => {
            if (!isMounted) return;
            await loadOpenTasks();
        };

        const handlePreview = () => {
            if (!isMounted) return;
            loadOpenTasks({ forcePreview: true });
        };

        loadInitialTasks();
        window.addEventListener(TASK_NOTICE_PREVIEW_EVENT, handlePreview);

        return () => {
            isMounted = false;
            window.removeEventListener(TASK_NOTICE_PREVIEW_EVENT, handlePreview);
        };
    }, [show, loadOpenTasks]);

    const taskCountText = useMemo(() => {
        if (tasks.length === 1) return "1 Open Task";
        return `${tasks.length} Open Tasks`;
    }, [tasks.length]);

    const openTasks = () => {
        setVisible(false);
        updateActiveSideButton("Tasks");
    };

    if (!visible || tasks.length === 0) return null;

    return (
        <div className="support-announcement-backdrop task-announcement-backdrop" role="dialog" aria-modal="true">
            <div className="support-announcement task-announcement">
                <div className="support-announcement__brand">
                    <img src={logo} alt="FBX" />
                    <div>
                        <h2>Your Tasks</h2>
                        <span className="task-announcement__count">{taskCountText}</span>
                    </div>
                </div>

                <div className="task-announcement__list">
                    {tasks.map((task) => (
                        <button type="button" className="task-announcement__task" key={task.taskId} onClick={openTasks}>
                            <strong>{task.title}</strong>
                            <span>{task.status || "Not Started"} · {Number(task.progress || 0)}% · Due {formatDate(task.dueDate)}</span>
                        </button>
                    ))}
                </div>

                <div className="support-announcement__actions">
                    <button type="button" className="btn btn-outline-secondary" onClick={() => setVisible(false)}>
                        Dismiss
                    </button>
                    <button type="button" className="btn btn-primary" onClick={openTasks}>
                        Open Task Manager
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TaskAnnouncement;
